<template>
  <div class="enterprise-wechat-approval-detail">
    <!-- 企微审批只保留审批流水号查询 -->
    <div class="section" style="margin-bottom: 10px;min-width: 1200px;">
      <el-form inline label-width="100px">
        <el-form-item :label="$t('CargoMsg.Cargo_ApprovalSerialNumber')">
          <el-input
            v-model="filter.documentProcessNo"
            :placeholder="$t('CargoMsg.Cargo_ApprovalSerialNumber')"
            clearable
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            {{ $t('CargoMsg.Cargo_Query') }}
          </el-button>
          <el-button @click="handleReset">
            {{ $t('CargoMsg.Cargo_Reset') }}
          </el-button>
        </el-form-item>
      </el-form>
    </div>
    <div class="section" style="flex: 1;padding: 0;">
      <cargo-ag-grid
        ref="enterpriseWechatGrid"
        :show-side-bar="false"
        :column-defs="columnDefs"
        :grid-options="gridOptions"
        :grid-params.sync="gridParams"
        :auto-fit-content="false"
        :row-data="rowData"
        style="min-width: 1200px;"
      />
      <cargo-pagination
        :total="filter.total"
        :page.sync="filter.pageNum"
        :limit.sync="filter.pageSize"
        @pagination="toQuery"
      />
    </div>
  </div>
</template>

<script>
import { getEnterpriseWechatApprovalInstances } from '@/api/enterpriseWechatApproval'
import { getLoading } from '@/utils/utilBaseElement'
import CargoAgGrid from '@/components/Cargo/Common/cargoAgGrid'

export default {
  name: 'EnterpriseWechatApprovalDetail',
  components: { CargoAgGrid },
  props: {
    status: { type: Number, default: 0 },
    type: { type: String, default: '' },
    documentConfigId: { type: Number, default: 0 },
    // 选中模板对应的动态表头配置
    templateColumns: { type: Array, default: () => [] }
  },
  data() {
    return {
      selectedRows: [],
      loading: null,
      filter: {
        total: 0,
        pageNum: 1,
        pageSize: 20,
        startDate: '',
        lastModified: '',
        officeIdList: [],
        workflowName: '',
        approvalDate: '',
        approveIdList: [],
        state: null,
        approvalTemplate: '',
        documentConfigIdList: [],
        documentProcessNo: '',
        applicationDimensions: [],
        viewSource: this.viewSource,
        requireCargoColumndefs: false
      },
      rowData: [],
      perConfigName: '',
      gridRefreshId: 0,
      configModule: 'ENTERPRISE_WECHAT_APPROVAL',
      gridParams: {},
      gridOptions: {
        rowSelection: 'single',
        suppressCellSelection: true,
        context: {
          componentParent: this
        },
        defaultColDef: {
          resizable: true,
          sortable: true,
          filter: true,
          // 给所有列设置一个最小宽度，避免表头过窄
          minWidth: 150
        }
      },
      columnDefs: []
    }
  },
  computed: {
    viewSource() {
      return this.type === 'look' ? 'enterpriseWechatInitiate' : 'enterpriseWechatHandle'
    },
    showBatchBtn() {
      return this.type === 'approve' && this.status === 0
    }
  },
  watch: {
    templateColumns: {
      immediate: true,
      handler() {
        this.buildColumnDefs()
      }
    }
  },
  methods: {
    // 根据固定列 + 模板动态列构建表头
    buildColumnDefs() {
      // 按表头字数粗略计算列宽，避免表头被挤压
      const calcWidthByHeader = (headerName) => {
        const text = (headerName || '').toString()
        const len = text.length || 1
        const baseMin =
          (this.gridOptions &&
            this.gridOptions.defaultColDef &&
            this.gridOptions.defaultColDef.minWidth) ||
          100
        // 每个字符按 16px 估算，额外加上一些内边距
        const width = len * 14 + 40
        const maxWidth = 400
        return Math.max(baseMin, Math.min(width, maxWidth))
      }

      const approvalSerialHeader = this.$t('CargoMsg.Cargo_ApprovalSerialNumber')
      const statusHeader = this.$t('CargoMsg.Cargo_Status')
      const initiatorHeader = this.$t('CargoMsg.Cargo_Initiator')
      const startTimeHeader = this.$t('CargoMsg.Cargo_StartTime')

      const fixedColumns = [
        {
          headerName: approvalSerialHeader,
          // 接口字段为 spNo
          field: 'spNo',
          colId: 'spNo',
          width: calcWidthByHeader(approvalSerialHeader)
        },
        {
          headerName: statusHeader,
          // 使用后端返回的中文状态文案
          field: 'statusText',
          colId: 'statusText',
          width: calcWidthByHeader(statusHeader)
        },
        {
          headerName: initiatorHeader,
          // 申请人名称
          field: 'applicantName',
          colId: 'applicantName',
          width: calcWidthByHeader(initiatorHeader)
        },
        {
          headerName: startTimeHeader,
          // 发起时间
          field: 'createTime',
          colId: 'createTime',
          width: calcWidthByHeader(startTimeHeader)
        }
      ]

      const dynamicColumns = (this.templateColumns || []).map(col => {
        // 后端 columns 建议字段：headerName / field
        const field = col.field || col.code || col.key
        const headerName = col.headerName || col.label || field
        return {
          headerName,
          colId: field,
          width: calcWidthByHeader(headerName),
          // 动态表单字段统一从 formData 中取值
          valueGetter(params) {
            const formData = (params.data && params.data.formData) || {}
            const value = formData[field]
            if (Array.isArray(value)) {
              return value.join(',')
            }
            return value == null ? '' : value
          }
        }
      })

      this.columnDefs = fixedColumns.concat(dynamicColumns)
    },
    init(documentProcessNo = '') {
      this.filter = {
        total: 0,
        pageNum: 1,
        pageSize: 20,
        startDate: '',
        lastModified: '',
        officeIdList: [],
        workflowName: '',
        approvalDate: '',
        approveIdList: [],
        state: null,
        approvalTemplate: '',
        documentConfigIdList: [],
        documentProcessNo: documentProcessNo,
        applicationDimensions: [],
        viewSource: this.viewSource,
        requireCargoColumndefs: false
      }
      // 未选择具体模板时，不加载实例列表
      if (!this.documentConfigId) {
        this.rowData = []
        return
      }
      if (this.status !== -1 || documentProcessNo) {
        const showDetail = !!documentProcessNo
        this.toQuery(showDetail)
      }
    },
    toQuery(showDetail = false) {
      // 未选择模板时不允许查询
      if (!this.documentConfigId) {
        if (this.$message) {
          this.$message.warning(this.$t('CargoMsg.Cargo_PleaseSelectTemplate'))
        }
        return
      }
      const params = {
        templateId: this.documentConfigId || '',
        spNo: this.filter.documentProcessNo || '',
        // 直接传后端状态枚举：0=全部,1=审批中,2=已通过,3=已驳回,4=已撤销
        status: this.status,
        pageNum: this.filter.pageNum,
        pageSize: this.filter.pageSize
      }

      this.loading = getLoading('enterprise-wechat-approval-detail')
      getEnterpriseWechatApprovalInstances(params).then(res => {
        const resp = res && res.data ? res.data : {}
        this.filter.total = resp.total || 0
        this.rowData = resp.list || []
        if (showDetail && this.rowData && this.rowData.length > 0 && this.$refs.enterpriseWechatGrid) {
          // 如需默认打开第一条详情，可在此处触发
        }
        this.applyCloseAndRefresh(this.filter.total)
      }).finally(() => {
        if (this.loading) {
          this.loading.close()
        }
      })
    },
    handleSearch() {
      // 查询时重置到第一页
      this.filter.pageNum = 1
      this.toQuery(false)
    },
    handleReset() {
      this.filter.documentProcessNo = ''
      this.filter.pageNum = 1
      this.toQuery(false)
    },
    applyCloseAndRefresh(total) {
      this.$emit('reloadCount', total)
    },
    changeSelected(list) {
      this.selectedRows = list
    }
  }
}
</script>

<style lang="scss" scoped>
.enterprise-wechat-approval-detail{
  .el-form{
    .el-form-item__content{
      width: 210px;
    }
    .el-date-editor--datetime{
      width: 210px !important;
    }
  }
}
</style>
